import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer, SlotInfo, View } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { addDays, endOfMonth, endOfWeek, format, parse, startOfMonth, startOfWeek, getDay, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import Select from "react-select";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Button,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import { CommitmentContext } from "../context/CommitmentContext";
import { LeadContext } from "../context/LeadContext";
import { GoogleCalendarContext } from "../context/GoogleCalendarContext";
import type {
    CommitmentResponse,
    CommitmentByWeekResponse,
    CreateCommitmentRequest,
    UpdateCommitmentRequest,
    RescheduleCommitmentRequest,
} from "../context/hooks/useCommitment";
import { useToast } from "../context/ToastContext";
import { GoogleEventDto } from "../context/hooks/useGoogleCalendar";

type RBCEvent = {
    id: string;
    title: string;
    start: Date;
    end: Date;
    resource: CommitmentResponse | null;
    isGoogle?: boolean;
    gId?: string;
};
type Option = { value: string; label: string };
type RangeArg = { start: Date; end: Date } | Date[];
type LocalEvent = RBCEvent & { isGoogle?: false; resource: CommitmentResponse };

function isLocalEvent(ev: RBCEvent): ev is LocalEvent {
    return !ev.isGoogle && ev.resource !== null;
}

const locales = { "pt-BR": ptBR } as const;
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
    getDay,
    locales,
});
const DnDCalendar = withDragAndDrop(Calendar);

const ptMessages = {
    allDay: "Dia inteiro",
    previous: "Anterior",
    next: "Próximo",
    today: "Hoje",
    month: "Mês",
    week: "Semana",
    day: "Dia",
    agenda: "Agenda",
    date: "Data",
    time: "Hora",
    event: "Evento",
    noEventsInRange: "Nenhum evento nesse período.",
    showMore: (total: number) => `+ Ver mais (${total})`,
};

function mapGoogleToRBC(events: GoogleEventDto[]): RBCEvent[] {
    return (events || []).map(ev => {
        const s = ev.start ? new Date(ev.start) : new Date();
        const e = ev.end ? new Date(ev.end) : new Date(s.getTime() + 30 * 60000);
        return {
            id: `g:${ev.id}`,
            title: ev.summary || "Evento",
            start: s,
            end: e,
            resource: null,
            isGoogle: true,
            gId: ev.id,
        };
    });
}

function toDate(date: string, time: string) {
    const [h, m] = time.split(":").map(Number);
    const base = parse(date, "yyyy-MM-dd", new Date());
    return new Date(base.getFullYear(), base.getMonth(), base.getDate(), h || 0, m || 0, 0, 0);
}

function toISODate(d: Date) {
    return format(d, "yyyy-MM-dd");
}

function toTime(d: Date) {
    return format(d, "HH:mm:ss");
}

function normTime(val: string) {
    if (/^\d{2}:\d{2}$/.test(val)) return val + ":00";
    if (/^\d{2}:\d{2}:\d{2}$/.test(val)) return val;
    try {
        const dt = parse(val, "HH:mm", new Date());
        return format(dt, "HH:mm:ss");
    } catch {
        return "00:00:00";
    }
}

function isInactiveStatus(s?: string) {
    const v = (s || "").toLowerCase();
    return v.includes("canc") || v.includes("conc");
}

function flattenWeek(data: CommitmentByWeekResponse[]): RBCEvent[] {
    const list: RBCEvent[] = [];
    data.forEach((w) => {
        (w.commitments || [])
            .filter((c) => !isInactiveStatus(c.afterMeetingStatus?.status))
            .forEach((c) => {
                const start = toDate(w.date, c.begin);
                const end = toDate(w.date, c.end);
                list.push({ id: c.id, title: c.title, start, end, resource: c });
            });
    });
    return list;
}

function priorityColor(p?: number) {
    if (!p) return "#4f46e5";
    if (p >= 4) return "#dc2626";
    if (p === 3) return "#ea580c";
    if (p === 2) return "#16a34a";
    return "#2563eb";
}

function overlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) {
    return aStart < bEnd && aEnd > bStart;
}

function visibleRangeFor(view: View, date: Date): RangeArg {
    if (view === "week") {
        const start = startOfWeek(date, { weekStartsOn: 0 });
        const end = endOfWeek(date, { weekStartsOn: 0 });
        return { start, end };
    }
    if (view === "month") {
        const start = startOfMonth(date);
        const end = endOfMonth(date);
        return { start, end };
    }
    const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const end = addDays(start, 0);
    return { start, end };
}

export default function AgendaPage() {
    const {
        weeklyCommitments,
        getByWeek,
        createCommitment,
        updateCommitment,
        rescheduleCommitment,
        deleteCommitment,
        setWeeklyCommitments,
    } = useContext(CommitmentContext);
    const { googleEvents, getGoogleEvents, connectGoogle } = useContext(GoogleCalendarContext);
    const showToast = useToast();

    const [view, setView] = useState<View>("week");
    const [date, setDate] = useState<Date>(new Date());
    const [events, setEvents] = useState<RBCEvent[]>([]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState<CommitmentResponse | null>(null);
    const slotRef = useRef<{ start: Date; end: Date } | null>(null);
    const rangeRef = useRef<RangeArg | null>(null);

    const beginEndFromRange = (r: RangeArg) => {
        if (Array.isArray(r)) {
            const first = r[0] as Date;
            const last = r[r.length - 1] as Date;
            return { begin: toISODate(first), end: toISODate(last) };
        }
        return { begin: toISODate(r.start), end: toISODate(r.end) };
    };

    const doFetchByRange = async (r: RangeArg) => {
        rangeRef.current = r;
        const { begin, end } = beginEndFromRange(r);
        const data = await getByWeek(begin, end);
        if (data) setWeeklyCommitments(data);
    };

    const refreshVisible = () => {
        const r = rangeRef.current ?? visibleRangeFor(view, date);
        doFetchByRange(r);
    };

    const fetchGoogleByRange = async (r: RangeArg) => {
        const { begin, end } = beginEndFromRange(r);
        const b = startOfDay(parse(begin, "yyyy-MM-dd", new Date())).toISOString();
        const e = endOfDay(parse(end, "yyyy-MM-dd", new Date())).toISOString();
        await getGoogleEvents(b, e);
    };

    const handleRangeChange = async (r: RangeArg) => {
        await doFetchByRange(r);
        await fetchGoogleByRange(r);
    };

    useEffect(() => {
        const start = startOfWeek(new Date(), { weekStartsOn: 0 });
        const end = addDays(start, 6);
        (async () => {
            await doFetchByRange({ start, end });
            await fetchGoogleByRange({ start, end });
        })();
    }, []);

    useEffect(() => {
        const base = flattenWeek(weeklyCommitments || []);
        setEvents([...base, ...mapGoogleToRBC(googleEvents)]);
    }, [weeklyCommitments, googleEvents]);

    const onRangeChange = (r: RangeArg) => {
        doFetchByRange(r);
    };

    const onSelectSlot = (slot: SlotInfo) => {
        slotRef.current = { start: slot.start as Date, end: slot.end as Date };
        setCreating(true);
    };

    const onSelectEvent = (e: RBCEvent) => {
        if (!isLocalEvent(e)) return;
        setEditing(e.resource);
    };

    const onDrop = async (args: { event: RBCEvent; start: Date; end: Date }) => {
        const { event, start, end } = args;

        if (!isLocalEvent(event)) return;
        const c = event.resource;

        if (!lock(c.id)) return;
        try {
            const oldStart = toDate(c.date, c.begin);
            const oldEnd = toDate(c.date, c.end);

            if (!sameYMD(start, oldStart)) {
                const ok = await updateOnce(c, start, end);
                if (ok) refreshVisible();
                return;
            }

            if (hasConflictExceptSelf(start, start, end, c.id)) {
                showToast("error", "Conflita com outro compromisso.");
                refreshVisible();
                return;
            }

            if (overlaps(start, end, oldStart, oldEnd)) {
                const ok = await updateWithPivotTx(c, start, end);
                if (ok) refreshVisible(); else refreshVisible();
                return;
            }

            const ok = await updateOnce(c, start, end);
            if (ok) refreshVisible();
        } finally {
            unlock(c.id);
        }
    };

    const onResize = async (args: { event: RBCEvent; start: Date; end: Date }) => {
        const { event, start, end } = args;

        if (!isLocalEvent(event)) return;
        const c = event.resource;

        if (!lock(c.id)) return;
        try {
            const oldStart = toDate(c.date, c.begin);
            if (!sameYMD(start, oldStart)) {
                const ok = await updateOnce(c, start, end);
                if (ok) refreshVisible();
                return;
            }
            if (hasConflictExceptSelf(start, start, end, c.id)) {
                showToast("error", "Conflita com outro compromisso.");
                refreshVisible();
                return;
            }
            const ok = overlaps(start, end, toDate(c.date, c.begin), toDate(c.date, c.end))
                ? await updateWithPivotTx(c, start, end)
                : await updateOnce(c, start, end);
            if (ok) refreshVisible(); else refreshVisible();
        } finally {
            unlock(c.id);
        }
    };

    const eventPropGetter = (e: RBCEvent) => {
        const bg = e.isGoogle ? "#0ea5e9" : priorityColor(e.resource?.priority);
        return { style: { backgroundColor: bg, borderRadius: 8, border: "none", color: "#fff", padding: 2 } };
    };

    const sameYMD = (a: Date, b: Date) => format(a, "yyyy-MM-dd") === format(b, "yyyy-MM-dd");
    const addMin = (d: Date, m: number) => new Date(d.getTime() + m * 60000);
    const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) => aStart < bEnd && aEnd > bStart;

    const hasConflictExceptSelf = (day: Date, start: Date, end: Date, excludeId: string) => {
        const key = format(day, "yyyy-MM-dd");
        return events.some(ev => ev.id !== excludeId && format(ev.start, "yyyy-MM-dd") === key && overlaps(start, end, ev.start, ev.end));
    };

    const findPivotNearTarget = (targetStart: Date, targetEnd: Date, excludeId: string): { start: Date; end: Date } | null => {
        const offsets = Array.from({ length: 25 }, (_, i) => (i - 12) * 5);
        for (const off of offsets) {
            const s = addMin(targetStart, off);
            const e = addMin(s, 5);
            if (!hasConflictExceptSelf(targetStart, s, e, excludeId) && !overlaps(s, e, targetStart, targetEnd)) {
                if (s.getHours() !== 0 || s.getMinutes() !== 0) return { start: s, end: e };
            }
        }
        for (let h = 7; h <= 20; h++) {
            const s = new Date(targetStart.getFullYear(), targetStart.getMonth(), targetStart.getDate(), h, 0, 0, 0);
            const e = addMin(s, 5);
            if (!hasConflictExceptSelf(targetStart, s, e, excludeId) && !overlaps(s, e, targetStart, targetEnd)) return { start: s, end: e };
        }
        return null;
    };

    const updateOnce = async (c: CommitmentResponse, start: Date, end: Date) => {
        const payload: UpdateCommitmentRequest = {
            id: c.id,
            leadId: c.lead?.id as string,
            title: c.title,
            date: toISODate(start),
            begin: toTime(start),
            end: toTime(end),
            commitmentTypeId: (c.commitmentType?.id as unknown as number) ?? 1,
            afterMeetingStatusId: (c.afterMeetingStatus?.id as unknown as number) ?? 0,
            priority: c.priority,
            link: c.link || undefined,
            address: c.address || undefined,
            comment: c.comment || undefined,
        };
        return await updateCommitment(payload);
    };

    const opLockRef = useRef<Record<string, boolean>>({});
    const lock = (id: string) => { if (opLockRef.current[id]) return false; opLockRef.current[id] = true; return true; };
    const unlock = (id: string) => { delete opLockRef.current[id]; };

    const updateWithPivotTx = async (c: CommitmentResponse, targetStart: Date, targetEnd: Date) => {
        const originalStart = toDate(c.date, c.begin);
        const originalEnd = toDate(c.date, c.end);
        const pivot = findPivotNearTarget(targetStart, targetEnd, c.id);
        if (!pivot) return null;
        const ok1 = await updateOnce(c, pivot.start, pivot.end);
        if (!ok1) return null;
        const ok2 = await updateOnce(c, targetStart, targetEnd);
        if (!ok2) {
            await updateOnce(c, originalStart, originalEnd);
            return null;
        }
        return ok2;
    };

    const components = useMemo(
        () => ({
            event: ({ event }: { event: RBCEvent }) => (
                <div className="truncate">
                    <div className="font-semibold leading-4">{event.title}</div>
                    <div className="text-xs opacity-80">
                        {event.resource?.lead?.name ?? (event.isGoogle ? "Google" : "Sem lead")}
                    </div>
                </div>
            ),
        }),
        []
    );

    return (
        <div className="p-4 space-y-4">
            <div className="flex flex-wrap gap-2 items-center justify-between">
                <div className="text-2xl font-bold">Agenda</div>
                <Button color="secondary" onClick={() => connectGoogle(window.location.href)}>
                    Conectar Google
                </Button>
            </div>
            <div className="rounded-2xl shadow overflow-hidden bg-white">
                <DnDCalendar
                    localizer={localizer}
                    events={events}
                    startAccessor={(e: any) => e.start}
                    endAccessor={(e: any) => e.end}
                    style={{ height: 680 }}
                    onRangeChange={handleRangeChange as any}
                    onNavigate={(d: Date) => { setDate(d); }}
                    date={date}
                    onView={(v: View) => {
                        setView(v);
                        const r = visibleRangeFor(v, date);
                        handleRangeChange(r);
                    }}
                    view={view}
                    selectable
                    resizable
                    onSelectSlot={onSelectSlot}
                    onSelectEvent={onSelectEvent as unknown as (e: object) => void}
                    onEventDrop={onDrop as unknown as () => void}
                    onEventResize={onResize as unknown as () => void}
                    messages={ptMessages as unknown as any}
                    eventPropGetter={eventPropGetter as unknown as any}
                    components={components as unknown as any}
                    popup
                />
            </div>

            <CreateCommitmentModal
                isOpen={creating}
                onClose={() => setCreating(false)}
                slot={slotRef.current}
                onCreated={() => refreshVisible()}
            />

            <EditCommitmentModal
                data={editing}
                onClose={() => setEditing(null)}
                onUpdated={() => refreshVisible()}
            />
        </div>
    );
}

function CreateCommitmentModal({
    isOpen,
    onClose,
    slot,
    onCreated,
}: {
    isOpen: boolean;
    onClose: () => void;
    slot: { start: Date; end: Date } | null;
    onCreated: () => void;
}) {
    const { createCommitment } = useContext(CommitmentContext);
    const { leads, getAllLeads } = useContext(LeadContext);
    const [loadingLeads, setLoadingLeads] = useState(false);
    const [allLeads, setAllLeads] = useState<any[]>([]);
    const [leadOptions, setLeadOptions] = useState<Option[]>([]);
    const [leadQuery, setLeadQuery] = useState("");
    const [lead, setLead] = useState<Option | null>(null);
    const [title, setTitle] = useState("");
    const [date, setDate] = useState("");
    const [begin, setBegin] = useState("");
    const [end, setEnd] = useState("");
    const [priority, setPriority] = useState(3);
    const [commitmentTypeId, setCommitmentTypeId] = useState<number>(1 as any);
    const [link, setLink] = useState("");
    const [address, setAddress] = useState("");
    const [comment, setComment] = useState("");

    const mapToOptions = (list: any[]) =>
        list.slice(0, 1000).map((l: any) => ({
            value: String(l?.leadId || l?.id || ""),
            label: String(l?.name || ""),
        }));

    const loadAllLeads = async () => {
        setLoadingLeads(true);
        const list = await getAllLeads();
        const arr = Array.isArray(list) ? list : ((list as any)?.items ?? []);
        setAllLeads(Array.isArray(arr) ? arr : []);
        setLoadingLeads(false);
    };

    const filterLeads = (q: string) => {
        const term = (q || "").toLowerCase();
        const baseCtx = Array.isArray(leads) ? leads : (((leads as any)?.items ?? []) as any[]);
        const source = allLeads.length ? allLeads : Array.isArray(baseCtx) ? baseCtx : [];
        const filtered = term
            ? source.filter((l: any) =>
                String(l?.name || "").toLowerCase().includes(term) ||
                String(l?.email || "").toLowerCase().includes(term) ||
                String(l?.phone || "").toLowerCase().includes(term)
            )
            : source;
        setLeadOptions(mapToOptions(filtered));
    };

    useEffect(() => {
        if (isOpen) {
            setLead(null);
            setTitle("");
            setPriority(3);
            setCommitmentTypeId(1 as any);
            setLink("");
            setAddress("");
            setComment("");
            setLeadQuery("");
            setDate((prev) => prev || (slot ? toISODate(slot.start) : toISODate(new Date())));
            setBegin((prev) => prev || (slot ? toTime(slot.start) : "09:00"));
            setEnd((prev) => prev || (slot ? toTime(slot.end) : "10:00"));
            loadAllLeads();
        }
    }, [isOpen, slot]);

    useEffect(() => {
        filterLeads(leadQuery);
    }, [allLeads, leads, leadQuery]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!lead || !lead.value) return;
        const leadSuffix = `Lead: ${lead?.label ?? ""}`.trim();
        const commentFinal = [String(comment || "").trim(), leadSuffix].filter(Boolean).join("\n");
        const payload: CreateCommitmentRequest = {
            leadId: String(lead.value),
            title,
            date,
            begin: normTime(begin),
            end: normTime(end),
            commitmentTypeId,
            priority,
            link: link || undefined,
            address: address || undefined,
            comment: commentFinal || undefined,
        };
        const ok = await createCommitment(payload as any);
        if (ok) {
            onCreated();
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} backdrop="static" size="lg">
            <ModalHeader toggle={onClose}>Novo compromisso</ModalHeader>
            <ModalBody>
                <Form onSubmit={submit}>
                    <Row>
                        <Col md="8">
                            <FormGroup>
                                <Label className="form-control-label">Lead</Label>
                                <Select
                                    options={leadOptions}
                                    value={lead}
                                    onChange={(opt: any) => setLead(opt as Option)}
                                    onInputChange={(v: string) => {
                                        setLeadQuery(v);
                                        return v;
                                    }}
                                    isLoading={loadingLeads}
                                    placeholder="Buscar lead pelo nome ou e-mail"
                                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                                    styles={{ menu: (p: any) => ({ ...p, zIndex: 9999 }), menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Prioridade</Label>
                                <Input type="select" value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
                                    <option value={1}>Baixa</option>
                                    <option value={2}>Normal</option>
                                    <option value={3}>Média</option>
                                    <option value={4}>Alta</option>
                                    <option value={5}>Crítica</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="8">
                            <FormGroup>
                                <Label className="form-control-label">Título</Label>
                                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Tipo</Label>
                                <Input type="number" value={commitmentTypeId} onChange={(e) => setCommitmentTypeId(Number(e.target.value))} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Data</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Início</Label>
                                <Input type="time" value={begin} onChange={(e) => setBegin(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Fim</Label>
                                <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Link</Label>
                                <Input value={link} onChange={(e) => setLink(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Endereço</Label>
                                <Input value={address} onChange={(e) => setAddress(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">Observações</Label>
                                <Input type="textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end gap-2">
                        <Button style={{ background: "#585858", color: "#fff" }} type="submit" disabled={!lead || !lead.value || !title || !date || !begin || !end}>Salvar</Button>
                        <Button color="secondary" onClick={onClose}>Cancelar</Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
}

function EditCommitmentModal({
    data,
    onClose,
    onUpdated,
}: {
    data: CommitmentResponse | null;
    onClose: () => void;
    onUpdated: () => void;
}) {
    const { updateCommitment, deleteCommitment } = useContext(CommitmentContext);
    const [title, setTitle] = useState("");
    const [leadName, setLeadName] = useState("");
    const [date, setDate] = useState("");
    const [begin, setBegin] = useState("");
    const [end, setEnd] = useState("");
    const [priority, setPriority] = useState(3);
    const [commitmentTypeId, setCommitmentTypeId] = useState<number>(1 as any);
    const [afterMeetingStatusId, setAfterMeetingStatusId] = useState<number>(0 as any);
    const [link, setLink] = useState("");
    const [address, setAddress] = useState("");
    const [comment, setComment] = useState("");

    useEffect(() => {
        if (!data) return;
        setTitle(data.title || "");
        setLeadName(data.lead?.name || "");
        setDate(data.date);
        setBegin(data.begin);
        setEnd(data.end);
        setPriority(data.priority ?? 3);
        setCommitmentTypeId((data.commitmentType?.id as any) ?? (1 as any));
        setAfterMeetingStatusId((data.afterMeetingStatus?.id as any) ?? (0 as any));
        setLink(data.link || "");
        setAddress(data.address || "");
        setComment(data.comment || "");
    }, [data]);

    if (!data) return null;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const leadSuffix = `Lead: ${data?.lead?.name ?? ""}`.trim();
        const hasSuffix = String(comment || "").includes(leadSuffix);
        const commentFinal = hasSuffix ? comment : [String(comment || "").trim(), leadSuffix].filter(Boolean).join("\n");
        const payload: UpdateCommitmentRequest = {
            id: data!.id,
            leadId: data!.lead?.id as string,
            title,
            date,
            begin,
            end,
            commitmentTypeId,
            afterMeetingStatusId,
            priority,
            link: link || undefined,
            address: address || undefined,
            comment: commentFinal || undefined,
        };
        const ok = await updateCommitment(payload);
        if (ok) {
            onUpdated();
            onClose();
        }
    };

    const removeIt = async () => {
        await deleteCommitment(data.id);
        onUpdated();
        onClose();
    };

    return (
        <Modal isOpen={!!data} toggle={onClose} backdrop="static" size="lg">
            <ModalHeader toggle={onClose}>Editar compromisso</ModalHeader>
            <ModalBody>
                <Form onSubmit={submit}>
                    <Row>
                        <Col md="8">
                            <FormGroup>
                                <Label className="form-control-label">Título</Label>
                                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Prioridade</Label>
                                <Input type="select" value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
                                    <option value={1}>Baixa</option>
                                    <option value={2}>Normal</option>
                                    <option value={3}>Média</option>
                                    <option value={4}>Alta</option>
                                    <option value={5}>Crítica</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Lead</Label>
                                <Input value={leadName} disabled />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label className="form-control-label">Tipo</Label>
                                <Input type="number" value={commitmentTypeId} onChange={(e) => setCommitmentTypeId(Number(e.target.value))} />
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label className="form-control-label">Status pós-reunião</Label>
                                <Input type="number" value={afterMeetingStatusId} onChange={(e) => setAfterMeetingStatusId(Number(e.target.value))} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Data</Label>
                                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Início</Label>
                                <Input type="time" value={begin} onChange={(e) => setBegin(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Fim</Label>
                                <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Link</Label>
                                <Input value={link} onChange={(e) => setLink(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Endereço</Label>
                                <Input value={address} onChange={(e) => setAddress(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">Observações</Label>
                                <Input type="textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-between">
                        <Button color="danger" type="button" onClick={removeIt}>Excluir</Button>
                        <div className="d-flex gap-2">
                            <Button style={{ background: "#585858", color: "#fff" }} type="submit">Salvar</Button>
                            <Button color="secondary" type="button" onClick={onClose}>Cancelar</Button>
                        </div>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
}